import type {
  ChatMessage,
  GamePlayerState,
  GameSnapshot,
  RoomEvent,
  RoomId,
  RoomState,
  ServerMessage,
} from 'shared'

export type OnlineSession = {
  playerId: string;
  roomId: RoomId;
  name: string;
  room: RoomState;
  chatHistory: ChatMessage[];
  pingMs: number | null;
  sendState: (state: GamePlayerState) => void;
  sendShoot: (x: number, y: number, vx: number, vy: number, opts?: { interpDelayMs?: number }) => void;
  sendKill: (victimId: string) => void;
  sendFlagCapture: (team: 'red' | 'blue') => void;
  sendLevelSolids: (solids: Array<{ x: number; y: number; w: number; h: number }>) => void;
  selectTeam: (team: 'red' | 'blue' | 'auto') => void;
  sendChat: (text: string) => void;
  deleteRoom: () => void;
  onRoomState: (handler: (room: RoomState) => void) => void;
  onSnapshot: (handler: (snapshot: GameSnapshot) => void) => void;
  onChat: (handler: (message: ChatMessage) => void) => void;
  onEvent: (handler: (event: RoomEvent) => void) => void;
  onRoomDeleted: (handler: (roomId: RoomId) => void) => void;
  onPing: (handler: (pingMs: number | null) => void) => void;
  close: () => void;
};

export const getDefaultServerUrl = () => {
  const params = new URLSearchParams(window.location.search)
  return params.get('server') ?? localStorage.getItem('serverUrl') ?? import.meta.env.VITE_SERVER_URL ?? 'http://localhost:9000'
}

// Reconnect: храним последнюю принятую сессию в localStorage. Сервер держит
// запись игрока в комнате RECONNECT_GRACE_MS (см. серверный index.ts, сейчас
// 5 минут) после разрыва WS. Клиентский TTL — чуть меньше, чтобы не пытаться
// resume на уже выгнанный playerId.
const SESSION_STORAGE_KEY = 'onlineSession'
const SESSION_TTL_MS = 4 * 60 * 1000

type StoredSession = { playerId: string; roomId: RoomId; expiresAtMs: number }

const readStoredSession = (): StoredSession | null => {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredSession>
    if (typeof parsed.playerId !== 'string' || typeof parsed.roomId !== 'string') return null
    if (typeof parsed.expiresAtMs !== 'number' || parsed.expiresAtMs < Date.now()) {
      localStorage.removeItem(SESSION_STORAGE_KEY)
      return null
    }
    return parsed as StoredSession
  } catch {
    return null
  }
}

const writeStoredSession = (playerId: string, roomId: RoomId) => {
  try {
    const payload: StoredSession = {
      playerId,
      roomId,
      expiresAtMs: Date.now() + SESSION_TTL_MS,
    }
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // localStorage может быть запрещён в private mode — не валим коннект
  }
}

const clearStoredSession = () => {
  try { localStorage.removeItem(SESSION_STORAGE_KEY) } catch {}
}

const toWsUrl = (httpUrl: string) => {
  const url = new URL(httpUrl)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.pathname = '/ws'
  url.search = ''
  return url.toString()
}

export type RoomsWatcher = {
  requestRooms: () => void;
  close: () => void;
};

const createPingTracker = (ws: WebSocket, onPing: (pingMs: number | null) => void) => {
  const pending = new Map<number, number>()
  let intervalId: number | null = null
  let stopped = false
  let listening = false

  const resetPending = () => {
    pending.clear()
    onPing(null)
  }

  const sendPing = () => {
    if (ws.readyState !== WebSocket.OPEN) return
    if (document.hidden) {
      resetPending()
      return
    }

    const clientTimeMs = performance.now()
    pending.set(clientTimeMs, clientTimeMs)
    for (const [sentAt] of pending) {
      if (clientTimeMs - sentAt > 5000) pending.delete(sentAt)
    }
    ws.send(JSON.stringify({ type: 'ping', clientTimeMs }))
  }

  const handleVisibilityChange = () => {
    if (document.hidden) {
      resetPending()
      return
    }

    if (intervalId !== null) sendPing()
  }

  return {
    start: () => {
      if (stopped) return
      if (!listening) {
        document.addEventListener('visibilitychange', handleVisibilityChange)
        listening = true
      }
      sendPing()
      intervalId ??= window.setInterval(sendPing, 1000)
    },
    handlePong: (clientTimeMs: number) => {
      const sentAt = pending.get(clientTimeMs)
      if (sentAt === undefined || document.hidden) return

      pending.delete(clientTimeMs)
      const rttMs = Math.max(0, performance.now() - sentAt)
      if (rttMs > 5000) return

      onPing(Math.round(rttMs))
    },
    stop: () => {
      stopped = true
      if (listening) {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
        listening = false
      }
      if (intervalId !== null) {
        window.clearInterval(intervalId)
        intervalId = null
      }
      pending.clear()
      onPing(null)
    },
  }
}

export const watchOnlineRooms = (arg: {
  serverUrl?: string;
  onRooms: (rooms: RoomState[]) => void;
  onPing?: (pingMs: number | null) => void;
  onError?: (error: Error) => void;
}): RoomsWatcher => {
  const ws = new WebSocket(toWsUrl(arg.serverUrl ?? getDefaultServerUrl()))
  const ping = createPingTracker(ws, pingMs => arg.onPing?.(pingMs))

  const requestRooms = () => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'watchRooms' }))
    }
  }

  ws.onmessage = event => {
    const message = JSON.parse(event.data) as ServerMessage

    if (message.type === 'welcome') {
      requestRooms()
      ping.start()
      return
    }

    if (message.type === 'pong') {
      ping.handlePong(message.clientTimeMs)
      return
    }

    if (message.type === 'roomsState') {
      arg.onRooms(message.rooms)
      return
    }

    if (message.type === 'error') {
      arg.onError?.(new Error(`${message.code}: ${message.message}`))
    }
  }

  ws.onerror = () => {
    arg.onError?.(new Error('Ошибка WebSocket списка лобби'))
  }
  ws.onclose = () => {
    ping.stop()
  }

  return {
    requestRooms,
    close: () => {
      ping.stop()
      ws.close()
    },
  }
}

export const connectOnline = async (arg: {
  roomId?: RoomId;
  name: string;
  serverUrl?: string;
}): Promise<OnlineSession> => {
  const serverUrl = arg.serverUrl ?? getDefaultServerUrl()
  const ws = new WebSocket(toWsUrl(serverUrl))
  const snapshotHandlers = new Set<(snapshot: GameSnapshot) => void>()
  const roomHandlers = new Set<(room: RoomState) => void>()
  const chatHandlers = new Set<(message: ChatMessage) => void>()
  const eventHandlers = new Set<(event: RoomEvent) => void>()
  const roomDeletedHandlers = new Set<(roomId: RoomId) => void>()
  const pingHandlers = new Set<(pingMs: number | null) => void>()
  let currentPingMs: number | null = null
  let session: OnlineSession | null = null
  const ping = createPingTracker(ws, pingMs => {
    currentPingMs = pingMs
    if (session) session.pingMs = pingMs
    for (const handler of pingHandlers) handler(pingMs)
  })

  return await new Promise<OnlineSession>((resolve, reject) => {
    let playerId = ''
    let settled = false
    const fail = (error: Error) => {
      if (settled) return
      settled = true
      window.clearTimeout(failTimer)
      ping.stop()
      reject(error)
      ws.close()
    }
    const failTimer = window.setTimeout(() => {
      fail(new Error('Сервер не ответил на WebSocket подключение'))
    }, 5000)

    ws.onerror = () => {
      fail(new Error('Ошибка WebSocket подключения'))
    }
    ws.onclose = () => {
      ping.stop()
      if (!settled) fail(new Error('WebSocket соединение закрыто'))
    }

    // Шлём hello сразу же на open, до createRoom/joinRoom. Если в localStorage
    // лежит свежий { playerId, roomId } и пользователь не пошёл явно в другую
    // комнату — отдадим серверу для resume; иначе hello без resumePlayerId,
    // чтобы хук всё равно был отправлен (потом включим авто-реконнект, не меняя
    // протокол ещё раз).
    let helloSent = false
    let resumed = false
    const sendHello = () => {
      if (helloSent) return
      helloSent = true
      const stored = readStoredSession()
      // Resume имеет смысл только если пользователь возвращается в ту же комнату
      // (или создаёт новую без явного roomId — типичный поток «Online Test»).
      const canResume = stored && (arg.roomId === undefined || arg.roomId === stored.roomId)
      ws.send(JSON.stringify({
        type: 'hello',
        resumePlayerId: canResume ? stored!.playerId : undefined,
      }))
    }
    ws.addEventListener('open', sendHello, { once: true })
    // Если WebSocket уже был успешно открыт к моменту привязки обработчика
    // (race) — отправим вручную.
    if (ws.readyState === WebSocket.OPEN) sendHello()

    ws.onmessage = event => {
      const message = JSON.parse(event.data) as ServerMessage

      if (message.type === 'welcome') {
        playerId = message.playerId
        ping.start()
        // welcome.room присутствует только если сервер успешно сделал resume
        // по нашему resumePlayerId. В этом случае пропускаем createRoom/joinRoom —
        // мы уже в комнате, дальше дождёмся roomState и соберём сессию.
        if (message.room) {
          resumed = true
          // Обновляем срок жизни локальной записи (на тот случай если приём
          // другого welcome без room тоже придёт случайно).
          writeStoredSession(message.playerId, message.room.roomId)
          return
        }
        // Если у нас был resumeId, но сервер пришёл с другим id — стэйл, чистим.
        const stored = readStoredSession()
        if (stored && stored.playerId !== playerId) clearStoredSession()
        const joinMessage = arg.roomId
          ? { type: 'joinRoom', roomId: arg.roomId, name: arg.name }
          : { type: 'createRoom', name: arg.name, maxPlayers: 10 }
        ws.send(JSON.stringify(joinMessage))
        return
      }

      if (message.type === 'roomState' && playerId) {
        for (const handler of roomHandlers) handler(message.room)
        if (!session) {
          window.clearTimeout(failTimer)
          settled = true
          session = {
            playerId,
            roomId: message.room.roomId,
            name: arg.name,
            room: message.room,
            chatHistory: message.chatHistory ?? [],
            pingMs: currentPingMs,
            sendState: state => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'playerState', state }))
              }
            },
            sendShoot: (x, y, vx, vy, opts) => {
              if (ws.readyState === WebSocket.OPEN) {
                // Прикладываем измеренный пинг и текущий interp-delay клиента —
                // сервер по ним отматывает мир для hitscan-а на момент выстрела.
                const clientPingMs = currentPingMs ?? 0
                ws.send(JSON.stringify({
                  type: 'playerShoot',
                  x, y, vx, vy,
                  clientPingMs,
                  interpDelayMs: opts?.interpDelayMs,
                }))
              }
            },
            sendKill: victimId => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'kill', victimId }))
              }
            },
            sendFlagCapture: team => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'flagCapture', team }))
              }
            },
            sendLevelSolids: solids => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'setLevelSolids', solids }))
              }
            },
            selectTeam: team => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'selectTeam', team }))
              }
            },
            sendChat: text => {
              const trimmed = text.trim()
              if (trimmed && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'chat', text: trimmed }))
              }
            },
            deleteRoom: () => {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'deleteRoom' }))
              }
            },
            onRoomState: handler => {
              roomHandlers.add(handler)
            },
            onSnapshot: handler => {
              snapshotHandlers.add(handler)
            },
            onChat: handler => {
              chatHandlers.add(handler)
            },
            onEvent: handler => {
              eventHandlers.add(handler)
            },
            onRoomDeleted: handler => {
              roomDeletedHandlers.add(handler)
            },
            onPing: handler => {
              pingHandlers.add(handler)
              handler(currentPingMs)
            },
            close: () => {
              ping.stop()
              // Явный выход: говорим серверу «покидаю комнату», чтобы он сделал
              // полный leave (а не soft-disconnect через ws.onclose). Иначе
              // запись игрока висит в комнате до RECONNECT_GRACE_MS, и при
              // случайном повторном входе мы бы возродились в той же сессии —
              // в данном UX это не то, что хочет пользователь.
              if (ws.readyState === WebSocket.OPEN) {
                try { ws.send(JSON.stringify({ type: 'leaveRoom' })) } catch {}
              }
              clearStoredSession()
              ws.close()
            },
          }
          // При resume сервер сам помнит команду игрока — повторного auto-balance не делаем,
          // иначе он мог бы перекинуть в другую команду (хотя на сервере есть guard, но
          // лишний WS-сообщение не нужно). При свежем входе — обычная авто-балансировка.
          if (!resumed) session.selectTeam('auto')
          // Запоминаем сессию для возможного resume после reload-а.
          writeStoredSession(playerId, message.room.roomId)
          resolve(session)
        } else {
          session.room = message.room
        }
        return
      }

      if (message.type === 'gameSnapshot') {
        for (const handler of snapshotHandlers) handler(message.snapshot)
        return
      }

      if (message.type === 'pong') {
        ping.handlePong(message.clientTimeMs)
        return
      }

      if (message.type === 'chatMessage') {
        for (const handler of chatHandlers) handler(message.message)
        return
      }

      if (message.type === 'event') {
        for (const handler of eventHandlers) handler(message.event)
        return
      }

      if (message.type === 'roomDeleted') {
        // Комната уничтожена — забываем resume на неё, чтобы при следующем
        // коннекте не пытаться возродить уже несуществующую сессию.
        clearStoredSession()
        for (const handler of roomDeletedHandlers) handler(message.roomId)
        ping.stop()
        ws.close()
        return
      }

      if (message.type === 'error') {
        fail(new Error(`${message.code}: ${message.message}`))
      }
    }
  })
}
